import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Crown, User, ChevronDown, ChevronRight, Loader2 } from "lucide-react";

export const ROLE_LABELS = {
  ta_recruiter: "TA Recruiter",
  ta_lead: "TA Lead",
  tag_lead: "TAG Lead",
  tag_head: "TAG Head",
  tenant_admin: "Tenant Admin",
  platform_owner: "Platform Owner",
  owner: "Tenant Admin",
  recruiter: "TA Recruiter",
};
const ROLE_CHIP = {
  ta_recruiter: "tp-chip tp-chip-indigo",
  ta_lead: "tp-chip tp-chip-cyan",
  tag_lead: "tp-chip tp-chip-emerald",
  tag_head: "tp-chip tp-chip-amber",
  tenant_admin: "tp-chip tp-chip-rose",
  platform_owner: "tp-chip tp-chip-rose",
};

const INDENT = 32; // px per depth level

/**
 * Flatten the server-returned tree into render rows with the metadata the UI
 * needs to draw proper L-shaped tree connectors:
 *   - depth: how far indented (0 = root)
 *   - isLastChild: am I my parent's last report? (controls the connector shape)
 *   - ancestorHasMore: for each ancestor depth, does that ancestor still have
 *     siblings below the current row? Used to decide whether to draw a
 *     continuing vertical rail at that depth.
 * Done iteratively so there's no recursive JSX pattern that would trip the
 * visual-edits Babel analyzer (hit this trap earlier).
 */
function flatten(tree, collapsed) {
  const rows = [];
  const stack = [];
  for (let i = tree.length - 1; i >= 0; i--) {
    stack.push({ node: tree[i], depth: 0, isLastChild: i === tree.length - 1, ancestorHasMore: [] });
  }
  while (stack.length) {
    const row = stack.pop();
    const children = row.node.reports || [];
    rows.push({
      id: row.node.id,
      node: row.node,
      depth: row.depth,
      isLastChild: row.isLastChild,
      ancestorHasMore: row.ancestorHasMore,
      childCount: children.length,
    });
    if (collapsed[row.node.id]) continue;
    const nextAncestors = [...row.ancestorHasMore, !row.isLastChild];
    for (let i = children.length - 1; i >= 0; i--) {
      stack.push({
        node: children[i],
        depth: row.depth + 1,
        isLastChild: i === children.length - 1,
        ancestorHasMore: nextAncestors,
      });
    }
  }
  return rows;
}

export default function OrgChart() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    api.get("/tenant/org-chart")
      .then((r) => setTree(r.data.tree || []))
      .catch(() => setTree([]))
      .finally(() => setLoading(false));
  }, []);
  const rows = useMemo(() => (tree ? flatten(tree, collapsed) : []), [tree, collapsed]);
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }));

  if (loading) {
    return <div className="py-10 flex items-center justify-center text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Building org chart…</div>;
  }
  if (!tree || tree.length === 0) {
    return <div className="py-10 text-center text-slate-500 text-sm">No team members yet. Invite your first team member above to see the org chart.</div>;
  }

  return (
    <div className="org-chart py-2" data-testid="org-chart">
      {rows.map((row) => {
        const n = row.node;
        const isAdmin = n.role === "tenant_admin" || n.role === "platform_owner";
        const isCollapsed = !!collapsed[n.id];
        return (
          <div key={row.id} className="relative flex items-stretch" data-testid={`org-node-${n.id}`} style={{ minHeight: 56 }}>
            {/* Ancestor rails — one column per ancestor depth */}
            {row.ancestorHasMore.map((hasMore, i) => (
              <div key={`rail-${i}`} className="flex-shrink-0 relative" style={{ width: INDENT }}>
                {hasMore && (
                  <div className="absolute bg-slate-200" style={{ left: INDENT / 2, top: 0, bottom: 0, width: 1 }} />
                )}
              </div>
            ))}
            {/* Connector column (only for non-root rows) — draws the L into this node */}
            {row.depth > 0 && (
              <div className="flex-shrink-0 relative" style={{ width: INDENT }}>
                {/* vertical: full-height for middle children, half-height for the last child */}
                <div
                  className="absolute bg-slate-200"
                  style={{
                    left: INDENT / 2,
                    top: 0,
                    height: row.isLastChild ? "50%" : "100%",
                    width: 1,
                  }}
                />
                {/* horizontal arm at mid-height */}
                <div
                  className="absolute bg-slate-200"
                  style={{ left: INDENT / 2, top: "50%", width: INDENT / 2, height: 1 }}
                />
              </div>
            )}
            {/* The node card */}
            <div className="flex-1 min-w-0 py-2">
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all">
                <button
                  type="button"
                  onClick={() => toggle(n.id)}
                  className={`w-5 h-5 grid place-items-center rounded hover:bg-slate-100 transition-colors ${row.childCount === 0 ? "opacity-0 pointer-events-none" : ""}`}
                  data-testid={`org-toggle-${n.id}`}
                  aria-label={isCollapsed ? "Expand" : "Collapse"}
                >
                  {row.childCount > 0 && (isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />)}
                </button>
                <div className={`w-9 h-9 rounded-full grid place-items-center flex-shrink-0 ${isAdmin ? "bg-amber-50 border border-amber-200" : "bg-indigo-50 border border-indigo-200"}`}>
                  {isAdmin ? <Crown className="w-4 h-4 text-amber-600" /> : <User className="w-4 h-4 text-indigo-600" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate text-sm">{n.name}</div>
                  <div className="text-xs text-slate-500 truncate">{n.email}</div>
                </div>
                <span className={`${ROLE_CHIP[n.role] || "tp-chip"} text-[11px] flex-shrink-0`}>{ROLE_LABELS[n.role] || n.role}</span>
                {row.childCount > 0 && (
                  <span className="text-[11px] text-slate-400 flex-shrink-0 ml-1 whitespace-nowrap">
                    {row.childCount} {row.childCount === 1 ? "report" : "reports"}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
